/**
 * The last thing a gardener does, if they do it.
 *
 * Deleting an account is the one action in Grow with no undo anywhere behind
 * it — no archive, no trash, no restore. So the dialog is built to be slow on
 * purpose:
 *
 * - It counts first. "This cannot be undone" asks someone to accept a
 *   consequence they cannot see; "eleven photographs, five plants, twenty-two
 *   notes" is the same sentence with the consequence in it.
 * - It asks for the word to be typed. A destructive button one click from a
 *   settings page is a button people press by accident; a button that needs a
 *   word typed into it is not.
 * - It says what survives. The newsletter is a separate thing with a separate
 *   unsubscribe, and quietly leaving it alone would be a surprise later.
 *
 * Used by: DeleteAccountPage (/delete-account) and AccountCard
 * (Profile > Account). The counts come from the backend before anything is
 * deleted (shared/api/users getAccountDeletionSummary); the delete itself is
 * one request, and the backend removes the rest.
 *
 * The backend cancels the Stripe subscription before it deletes anything;
 * when Stripe cannot be reached it answers 503 and deletes nothing. That, any
 * other 5xx, or no answer at all says the standard sentence (D31,
 * lib/errorMessage), reported to Sentry, with no "try again"; a refusal keeps
 * profile.account.deleteDialog.failed. Counts that could not be read for the
 * same reason say the standard sentence in place of countsUnavailable.
 *
 * @module DeleteAccountDialog
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { toast } from '@/shared/components/ui/use-toast';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import {
  getAccountDeletionSummary, deleteAccount, type AccountDeletionSummary,
} from '@/shared/api/users';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';

/** Whether the dialog is open, and how it is closed. */
interface DeleteAccountDialogProps {
  /** Whether the dialog is showing; the counts are read again each time it opens. */
  isOpen: boolean;
  /** Asked to open or close it; not called while the delete is under way. */
  onOpenChange: (open: boolean) => void;
}

/**
 * One line per kind of thing, and only for the kinds that exist.
 *
 * A garden with no notes should not be told it is about to lose zero notes —
 * an accurate list of nothing is still noise, and it makes the numbers that
 * matter harder to read. Old saved planting timelines are deleted with the
 * rest but not listed: the timeline is retired, and its name with it.
 */
const SummaryList: React.FC<{ summary: AccountDeletionSummary }> = ({ summary }) => {
  const { reactTranslate: t } = useTranslation();

  const lines: Array<[number, string]> = [
    [summary.photos, t('profile.account.deleteDialog.countPhotos')],
    [summary.plants, t('profile.account.deleteDialog.countPlants')],
    [summary.notes, t('profile.account.deleteDialog.countNotes')],
    [summary.locations, t('profile.account.deleteDialog.countLocations')],
  ];

  const present = lines.filter(([count]) => count > 0);

  if (present.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('profile.account.deleteDialog.nothingStored')}
      </p>
    );
  }

  return (
    <ul className="space-y-1 text-sm">
      {present.map(([count, label]) => (
        <li key={label} className="flex items-baseline gap-2">
          <span className="min-w-[2.5rem] text-right font-semibold tabular-nums">{count}</span>
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
};

/** The account deletion dialog: what will go, the word to type, and the button. */
export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  isOpen, onOpenChange,
}) => {
  const { reactTranslate: t } = useTranslation();
  const { mysqlUser, logout } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState<AccountDeletionSummary | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  /** Why the counts could not be read, so a failure on our side says the standard sentence. */
  const [loadError, setLoadError] = useState<unknown>(null);
  const [typed, setTyped] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const userId = mysqlUser?.user_id;

  // Counted each time the dialog opens rather than once: a gardener may well
  // have opened this, backed out, added a photograph and come back.
  useEffect(() => {
    if (!isOpen || !userId) return;

    let cancelled = false;
    setSummary(null);
    setLoadFailed(false);
    setLoadError(null);
    setTyped('');

    getAccountDeletionSummary(userId)
      .then((result) => { if (!cancelled) setSummary(result); })
      .catch((err: unknown) => { if (!cancelled) { setLoadError(err); setLoadFailed(true); } });

    return () => { cancelled = true; };
  }, [isOpen, userId]);

  /** The word to type, in English whatever language the rest of the dialog is in: the server's (limits.DELETE_CONFIRM_WORD); none can be confirmed before the app has heard it. */
  const word = useGrowLimits().DELETE_CONFIRM_WORD;
  const confirmed = word !== undefined && typed.trim().toUpperCase() === word;

  /** Deletes the account once the word is typed, then signs out and goes to the start. */
  const handleDelete = async (): Promise<void> => {
    if (!userId || !confirmed || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteAccount(userId);
    } catch (err) {
      setIsDeleting(false);
      // A failure on our side (a 5xx, including the 503 when Stripe could not
      // be reached to cancel the subscription first, or no answer at all) is
      // the standard sentence, reported, with no "try again" (D31). Nothing
      // was deleted either way.
      toast({
        // On our side (a 503 when Stripe cannot be reached, say): nothing was
        // deleted, then the standard sentence.
        title: isServerFailure(err) ? t('profile.account.deleteDialog.notDeleted') : t('sitewide.error'),
        description: errorMessage(err, t('profile.account.deleteDialog.failed')),
        variant: 'destructive',
      });
      return;
    }
    // The account is gone; the session in this tab still thinks it exists.
    // Signing out is what makes the app agree with the server, and it lands
    // on the login page, which is the only page left that means anything.
    //
    // The confirmation travels with the navigation rather than as a toast.
    // A toast raised here had nowhere to appear — the Toaster unmounts with
    // the rest of the app a line earlier — so it waited in the module-level
    // store and surfaced on the next screen that had one, which was this
    // person signing up again half a minute later.
    //
    // The account is already gone by here, so a sign-out that fails must not
    // fall into "your account was not deleted": the start page is still
    // where they go.
    try { await logout(); } catch { /* the account is gone; the start page follows */ }
    navigate('/', { replace: true, state: { accountDeleted: true } });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!isDeleting) onOpenChange(open); }}>
      {/* Three bands: a fixed heading, a body that scrolls, and a foot that
          does not. On a short screen the counts are long enough to push the
          buttons past the bottom edge, and a confirmation you cannot reach is
          the same bug the photo screen had. The word to type stays down here
          with the buttons, so it is never the thing you have to scroll for. */}
      <DialogContent scrollBody={false} className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle>{t('profile.account.deleteDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('profile.account.deleteDialog.permanent')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <p className="mb-2 text-sm font-medium">
              {t('profile.account.deleteDialog.willRemove')}
            </p>
            {summary && <SummaryList summary={summary} />}
            {!summary && !loadFailed && (
              <p className="text-sm text-muted-foreground">{t('sitewide.loading')}</p>
            )}
            {loadFailed && (isServerFailure(loadError) ? (
              <ServerFailureNote error={loadError} where="DeleteAccountDialog" className="text-sm text-muted-foreground" />
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('profile.account.deleteDialog.countsUnavailable')}
              </p>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            {t('profile.account.deleteDialog.newsletterNote')}
          </p>
        </div>

        <div className="space-y-3 border-t px-6 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="delete-account-confirm">
              {t('profile.account.deleteDialog.typeToConfirm', { word: word ?? '' })}
            </Label>
            <Input
              id="delete-account-confirm"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              placeholder={word}
              disabled={isDeleting}
            />
          </div>

          <ActionRow cancelLabel={t('sitewide.cancel')} disabled={isDeleting} onCancel={() => onOpenChange(false)}>
            <Button variant="destructive" busy="deleting" loading={isDeleting} onClick={handleDelete} disabled={!confirmed}>
              {t('profile.account.deleteDialog.confirmButton')}
            </Button>
          </ActionRow>
        </div>
      </DialogContent>
    </Dialog>
  );
};
